import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../i18n/LanguageContext';
import { Linkedin, Facebook, Instagram, Youtube, ArrowRight, Download, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT = {
  fr: {
    tagline: "Firme de développement immobilier — Analyse, faisabilité, coordination et services techniques au Canada et aux États-Unis.",
    brochureLabel: "Brochure PLEXTERRA",
    brochureCta: "Télécharger",
    cols: {
      dev: {
        title: "Développement immobilier",
        items: [
          { label: "Analyse stratégique de terrain", to: "/services#analyse-terrain" },
          { label: "Étude de faisabilité", to: "/services#faisabilite" },
          { label: "Optimisation financière", to: "/services#optimisation-financiere" },
          { label: "Simulation SCHL / CMHC", to: "/services#schl" },
          { label: "Coordination de développement", to: "/services#coordination" }
        ]
      },
      tech: {
        title: "Services techniques",
        items: [
          { label: "Inspection préachat", to: "/services#inspection-preachat" },
          { label: "Inspection multilogement", to: "/services#inspection-multilogement" },
          { label: "Loi 16 & fonds de prévoyance", to: "/services#loi-16" },
          { label: "Plans de conception", to: "/services#plans" },
          { label: "Gestion de projet", to: "/services#gestion-projet" }
        ]
      },
      firm: {
        title: "La firme",
        items: [
          { label: "À propos", to: "/about" },
          { label: "Équipe", to: "/team" },
          { label: "Technologies", to: "/technologies" },
          { label: "Projets", to: "/projets" },
          { label: "Carrières", to: "/careers" },
          { label: "Blog", to: "/blog" },
          { label: "Contact", to: "/contact" }
        ]
      },
      compliance: {
        title: "Conformité",
        items: [
          { label: "Licences & autorités", to: "/licensing" },
          { label: "Politique de confidentialité", to: "/politique-confidentialite" },
          { label: "Conditions générales", to: "/conditions-generales" }
        ]
      }
    },
    newsletter: {
      title: "Recevez nos analyses",
      subtitle: "Tendances de marché, études sectorielles et opportunités d'investissement.",
      placeholder: "Votre adresse courriel",
      cta: "S'inscrire",
      loading: "…",
      success: "Merci !"
    },
    legal: ["Politique de confidentialité", "Conditions générales"],
    legalLinks: ["/politique-confidentialite", "/conditions-generales"],
    rights: "Tous droits réservés."
  },
  en: {
    tagline: "Développement immobilier firm — Analysis, feasibility, coordination and technical services across Canada and the United States.",
    brochureLabel: "PLEXTERRA Brochure",
    brochureCta: "Download",
    cols: {
      dev: {
        title: "Développement immobilier",
        items: [
          { label: "Strategic land analysis", to: "/services#analyse-terrain" },
          { label: "Feasibility study", to: "/services#faisabilite" },
          { label: "Financial optimization", to: "/services#optimisation-financiere" },
          { label: "CMHC / SCHL simulation", to: "/services#schl" },
          { label: "Development coordination", to: "/services#coordination" }
        ]
      },
      tech: {
        title: "Technical Services",
        items: [
          { label: "Pre-purchase inspection", to: "/services#inspection-preachat" },
          { label: "Multifamily inspection", to: "/services#inspection-multilogement" },
          { label: "Bill 16 & reserve fund", to: "/services#loi-16" },
          { label: "Design plans", to: "/services#plans" },
          { label: "Project management", to: "/services#gestion-projet" }
        ]
      },
      firm: {
        title: "The firm",
        items: [
          { label: "About", to: "/about" },
          { label: "Team", to: "/team" },
          { label: "Technologies", to: "/technologies" },
          { label: "Projects", to: "/projets" },
          { label: "Careers", to: "/careers" },
          { label: "Blog", to: "/blog" },
          { label: "Contact", to: "/contact" }
        ]
      },
      compliance: {
        title: "Compliance",
        items: [
          { label: "Licensing & Authorities", to: "/licensing" },
          { label: "Privacy Policy", to: "/privacy-policy" },
          { label: "Terms of Service", to: "/terms-of-service" }
        ]
      }
    },
    newsletter: {
      title: "Get our perspectives",
      subtitle: "Market trends, sector studies and investment opportunities.",
      placeholder: "Your email address",
      cta: "Subscribe",
      loading: "…",
      success: "Thank you!"
    },
    legal: ["Privacy Policy", "Terms of Service"],
    legalLinks: ["/privacy-policy", "/terms-of-service"],
    rights: "All rights reserved."
  }
};

const SOCIALS = [
  { icon: Linkedin, href: "https://www.linkedin.com/company/plexterra", label: "LinkedIn" },
  { icon: Facebook, href: "https://www.facebook.com/plexterra", label: "Facebook" },
  { icon: Instagram, href: "https://www.instagram.com/plexterra", label: "Instagram" },
  { icon: Youtube, href: "https://www.youtube.com/@plexterra", label: "YouTube" }
];

const Footer = () => {
  const { language } = useLanguage();
  const c = CONTENT[language] || CONTENT.en;
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email, language });
      setStatus('success');
      setEmail('');
      setTimeout(() => setStatus('idle'), 3000);
    } catch {
      setStatus('idle');
    }
  };

  return (
    <footer className="bg-[#0B1E3F] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-16">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <img
                src="/plexterra-logo-dark.png"
                alt="PLEXTERRA"
                className="w-16 h-16 object-contain"
              />
              <div className="flex flex-col leading-none">
                <span className="font-serif text-xl tracking-[0.25em]">PLEXTERRA</span>
                <span className="text-[10px] tracking-[0.3em] text-white/50 mt-1 uppercase">Développement immobilier</span>
              </div>
            </Link>
            <p className="text-sm text-white/60 leading-relaxed max-w-md mb-8">{c.tagline}</p>
            <div className="flex items-center gap-3">
              {SOCIALS.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#C6A15A] hover:bg-[#C6A15A]/10 transition-colors"
                  data-testid={`social-${s.label.toLowerCase()}`}
                >
                  <s.icon className="w-4 h-4" strokeWidth={1.5} />
                </a>
              ))}
            </div>
          </div>

          {/* Columns */}
          {Object.entries(c.cols).map(([key, col]) => (
            <div key={key}>
              <h4 className="text-[#C6A15A] text-xs tracking-[0.2em] uppercase mb-5">{col.title}</h4>
              <ul className="space-y-3">
                {col.items.map((it, i) => (
                  <li key={i}>
                    <Link to={it.to} className="text-sm text-white/70 hover:text-white transition-colors">
                      {it.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        <div className="border-t border-white/10 pt-10 mb-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h4 className="font-serif text-2xl mb-2">{c.newsletter.title}</h4>
              <p className="text-sm text-white/60">{c.newsletter.subtitle}</p>
            </div>
            <form onSubmit={handleSubscribe} className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={c.newsletter.placeholder}
                className="flex-1 px-4 py-3 bg-white/5 border border-white/20 text-white text-sm rounded-sm focus:outline-none focus:border-[#C6A15A] placeholder:text-white/40"
                data-testid="newsletter-email"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#C6A15A] text-[#0B1E3F] text-sm font-medium rounded-sm hover:bg-white transition-colors disabled:opacity-50"
                data-testid="newsletter-submit"
              >
                {status === 'loading' ? c.newsletter.loading : status === 'success' ? c.newsletter.success : c.newsletter.cta}
                <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        {/* Brochure download — bilingual */}
        <div className="border-t border-white/10 pt-6 pb-6 mb-4" data-testid="footer-brochure">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-8">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 border border-[#C6A15A]/40 flex items-center justify-center">
                <FileText className="w-4 h-4 text-[#C6A15A]" strokeWidth={1.5} />
              </div>
              <span className="text-xs tracking-[0.25em] uppercase text-[#C6A15A]">
                {c.brochureLabel}
              </span>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="/brochures/brochure-en.png"
                download="PLEXTERRA-Brochure-EN.png"
                className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-xs tracking-widest uppercase text-white/80 hover:border-[#C6A15A] hover:text-white transition-colors"
                data-testid="footer-brochure-download-en"
              >
                {c.brochureCta} — EN
                <Download className="w-3.5 h-3.5" strokeWidth={2} />
              </a>
              <a
                href="/brochures/brochure-fr.png"
                download="PLEXTERRA-Brochure-FR.png"
                className="inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-xs tracking-widest uppercase text-white/80 hover:border-[#C6A15A] hover:text-white transition-colors"
                data-testid="footer-brochure-download-fr"
              >
                {c.brochureCta} — FR
                <Download className="w-3.5 h-3.5" strokeWidth={2} />
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/50">
            © {new Date().getFullYear()} PLEXTERRA. {c.rights}
          </p>
          <div className="flex items-center gap-6">
            {c.legal.map((label, i) => (
              <Link key={i} to={c.legalLinks[i]} className="text-xs text-white/50 hover:text-white transition-colors">
                {label}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
